import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Bell, Zap, Plus, Download, Loader2 } from 'lucide-react'
import { AdImpactClient } from '../../lib/alertClientsApi'
import AdImpactFilterBar from './AdImpactFilterBar'
import AlertClientTable from './AlertClientTable'
import AdImpactAddClientsModal from './AdImpactAddClientsModal'
import { exportFilteredAlertClients } from './adimpactExportCsv'

const DEFAULT_FILTERS = {
  search: '',
  office: '',
  state: '',
  account_executive: '',
  election: '',
  year: '',
  delta_alerts: 'all',
  admo_alerts: 'all',
  admo_plus_alerts: 'all',
  ctv_alerts: 'all',
  dashboards: 'all',
  digital_dashboards: 'all',
}

const YELLOW = '#eab308'
const YELLOW_HOVER = '#facc15'
const PAGE_BG = '#f9fafb'

export default function AdImpactAlertsPanel() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [showAdd, setShowAdd] = useState(false)

  const loadClients = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true)
    try {
      const data = await AdImpactClient.list()
      setClients(data || [])
    } catch (e) {
      console.error(e)
      setClients([])
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadClients({ showLoading: true })
  }, [loadClients])

  const refresh = useCallback(() => {
    loadClients({ showLoading: false })
  }, [loadClients])

  const uniqueAEs = useMemo(() => {
    const set = new Set(clients.map(c => c.account_executive).filter(Boolean))
    return [...set].sort()
  }, [clients])

  const uniqueElections = useMemo(() => {
    const set = new Set(clients.map(c => c.election).filter(Boolean))
    return [...set].sort()
  }, [clients])

  const uniqueYears = useMemo(() => {
    const set = new Set(clients.map(c => c.year).filter(Boolean))
    return [...set].sort()
  }, [clients])

  const filtered = useMemo(() => {
    return clients.filter(c => {
      if (filters.search) {
        const s = filters.search.toLowerCase()
        const name = (c.client_name || '').toLowerCase()
        const email = (c.email || '').toLowerCase()
        if (!name.includes(s) && !email.includes(s)) return false
      }
      if (filters.office && c.office !== filters.office) return false
      if (filters.state && c.state !== filters.state) return false
      if (filters.account_executive && c.account_executive !== filters.account_executive) return false
      if (filters.election && c.election !== filters.election) return false
      if (filters.year && c.year !== filters.year) return false

      const boolFields = ['delta_alerts', 'admo_alerts', 'admo_plus_alerts', 'ctv_alerts', 'dashboards', 'digital_dashboards']
      for (const bf of boolFields) {
        if (filters[bf] === 'yes' && !c[bf]) return false
        if (filters[bf] === 'no' && c[bf]) return false
      }
      return true
    })
  }, [clients, filters])

  const totalCount = clients.length

  const outlineBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 12px',
    fontSize: 13,
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  const primaryBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 12px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    borderRadius: 8,
    backgroundColor: YELLOW,
    color: '#000',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: PAGE_BG,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          backgroundColor: '#000',
          borderBottom: `4px solid ${YELLOW}`,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: '0 auto',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                backgroundColor: YELLOW,
                borderRadius: 8,
                padding: 8,
                display: 'flex',
              }}
            >
              <Bell size={24} color="#000" strokeWidth={2.5} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>Alert Tracker</h1>
              <p
                style={{
                  margin: '2px 0 0',
                  fontSize: 11,
                  fontWeight: 600,
                  color: YELLOW,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                AdImpact Alerts Management
              </p>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              backgroundColor: '#1f2937',
              borderRadius: 8,
              padding: '8px 16px',
            }}
          >
            <Zap size={16} color={YELLOW} />
            <span style={{ fontSize: 13, color: '#d1d5db' }}>Total Clients</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{totalCount}</span>
          </div>
        </div>
      </header>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 1400,
          margin: '0 auto',
          width: '100%',
          padding: '24px 24px 16px',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Alert Tracker</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              style={{ ...outlineBtn, opacity: filtered.length === 0 ? 0.5 : 1, cursor: filtered.length === 0 ? 'not-allowed' : 'pointer' }}
              disabled={filtered.length === 0}
              onClick={() => exportFilteredAlertClients(filtered)}
            >
              <Download size={16} /> Export CSV
            </button>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => setShowAdd(true)}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = YELLOW_HOVER)}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = YELLOW)}
            >
              <Plus size={16} /> Add Clients
            </button>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          <AdImpactFilterBar
            filters={filters}
            setFilters={setFilters}
            uniqueAEs={uniqueAEs}
            uniqueElections={uniqueElections}
            uniqueYears={uniqueYears}
            filteredCount={filtered.length}
          />
        </div>

        <div style={{ flex: 1, minHeight: 0, marginTop: 16, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
              <Loader2 size={28} color={YELLOW} style={{ animation: 'adimpact-panel-spin 0.8s linear infinite' }} />
            </div>
          ) : (
            <AlertClientTable clients={filtered} onRefresh={refresh} />
          )}
        </div>
      </div>

      <style>{`
        @keyframes adimpact-panel-spin { to { transform: rotate(360deg); } }
      `}</style>

      <AdImpactAddClientsModal open={showAdd} onOpenChange={setShowAdd} onSuccess={refresh} />
    </div>
  )
}
